import React, { useState, useMemo } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Share2, Link as LinkIcon, Mail, QrCode, X, Check, ImageIcon } from 'lucide-react';
import { Button } from './ui/button';
import { canonicalUrl } from '../utils/canonicalUrl';

/**
 * Universal share + QR widget.
 *
 * - Uses `navigator.share` if available (mobile) — fired by tapping the
 *   "Share" pill at the end of the inline row.
 * - Otherwise renders an inline row of distinct, brand-coloured icons so
 *   users can clearly distinguish WhatsApp / SMS / Telegram / Email / X.
 *
 * Props:
 *   url     — absolute URL to share. Defaults to window.location.href.
 *   title   — short title used as email subject / tweet text.
 *   text    — longer description used as email body / WhatsApp message.
 *   variant — 'inline' (small icon row) | 'button' (single pill + dropdown)
 *                                       | 'qr-only' (just the QR code, inline)
 *   compact — if true, hide the trailing "Share" button AND the QR
 *             toggle (used when a sibling component like LocalizedShareRow
 *             already provides those affordances).
 */

// Inline brand SVGs so WhatsApp and Telegram are visually distinct from
// each other and from the generic "messaging bubble". Stroke-free, fill
// uses the brand colour the parent passes via `currentColor`.
const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M17.5 14.4c-.3-.1-1.7-.8-1.9-.9-.3-.1-.5-.1-.7.1-.2.3-.8.9-1 1.1-.2.2-.4.2-.6.1-.3-.1-1.2-.4-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6.1-.1.3-.4.4-.5.1-.2.2-.3.2-.5.1-.2 0-.4 0-.5-.1-.1-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.4 0 1.4 1 2.8 1.2 3 .1.2 2 3 4.8 4.2 2.8 1.2 2.8.8 3.3.8.5-.1 1.7-.7 1.9-1.3.2-.7.2-1.2.2-1.3-.1-.2-.3-.3-.6-.4zM12 2C6.5 2 2 6.5 2 12c0 1.9.5 3.7 1.5 5.3L2 22l4.8-1.5c1.5.8 3.3 1.3 5.2 1.3 5.5 0 10-4.5 10-10S17.5 2 12 2zm0 18.2c-1.7 0-3.3-.5-4.7-1.3l-.3-.2-3.5 1 1-3.4-.2-.4c-.9-1.4-1.4-3.1-1.4-4.9 0-5 4.1-9 9-9s9 4.1 9 9-4 9.2-8.9 9.2z" />
  </svg>
);

const TelegramIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
  </svg>
);

const SMSIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-9 9H9V9h2v2zm4 0h-2V9h2v2zm4 0h-2V9h2v2z" />
  </svg>
);

export const ShareButtons = ({
  url,
  title = 'Amanah by Zakat.International — free Islamic financial tools',
  text = 'Free, scholarly Islamic Zakat, Inheritance and Will calculators. Saved only in your browser. Verify with your local scholar.',
  variant = 'inline',
  compact = false,
  // When true, skip the standalone Poster + QR affordances (used when a
  // sibling LocalizedShareRow already renders those on the same line, so
  // we avoid duplication + mobile wrap).
  hideExtras = false,
  testid = 'share',
}) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Always canonicalize to https://zakat.international so shared links
  // never leak the *.preview.emergentagent.com preview URL.
  const finalUrl = url || canonicalUrl();
  const POSTER_URL = `${finalUrl.replace(/\/$/, '').replace(/\/[^/]*$/, '')}/share-poster.png`;
  // The shared write-up now includes the poster URL so WhatsApp / Telegram
  // clients auto-embed a preview and recipients can save the poster.
  const richText = `${text}\n\nPoster: ${POSTER_URL}`;
  const encU = encodeURIComponent(finalUrl);
  const encT = encodeURIComponent(title);
  const encX = encodeURIComponent(richText);
  const encRichWA = encodeURIComponent(`${text}\n\n${finalUrl}\n\nPoster: ${POSTER_URL}`);

  // Each entry now carries its OWN brand colour + hover background so users
  // can immediately tell WhatsApp from Telegram from SMS at a glance.
  const links = useMemo(() => ([
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      Icon: WhatsAppIcon,
      href: `https://wa.me/?text=${encRichWA}`,
      colour: 'text-[#25D366]',
      hoverBg: 'hover:bg-[#25D366]/10 hover:border-[#25D366]/60',
    },
    {
      id: 'sms',
      label: 'SMS / Messages',
      Icon: SMSIcon,
      href: `sms:?&body=${encRichWA}`,
      colour: 'text-[#0D4733]',
      hoverBg: 'hover:bg-[#F3EFEA] hover:border-[#0D4733]',
    },
    {
      id: 'telegram',
      label: 'Telegram',
      Icon: TelegramIcon,
      href: `https://t.me/share/url?url=${encU}&text=${encX}`,
      colour: 'text-[#229ED9]',
      hoverBg: 'hover:bg-[#229ED9]/10 hover:border-[#229ED9]/60',
    },
    {
      id: 'email',
      label: 'Email',
      Icon: Mail,
      href: `mailto:?subject=${encT}&body=${encX}%0A%0A${encU}`,
      colour: 'text-amber-700',
      hoverBg: 'hover:bg-amber-50 hover:border-amber-400',
    },
  ]), [encU, encT, encX, encRichWA]);

  const handleNativeShare = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (typeof navigator !== 'undefined' && navigator.share) {
      // Try to attach the poster as an image File first (Web Share Level 2).
      try {
        let files;
        try {
          const res = await fetch('/share-poster.png');
          const blob = await res.blob();
          const file = new File([blob], 'zakat-international-poster.png', { type: 'image/png' });
          if (navigator.canShare && navigator.canShare({ files: [file] })) files = [file];
        } catch (_e) { /* fallback to URL-only */ }
        await navigator.share(
          files
            ? { title, text: `${text}\n\n${finalUrl}`, files }
            : { title, text: richText, url: finalUrl }
        );
        return;
      } catch { /* user cancelled — fall through to popover */ }
    }
    setOpen((v) => !v);
  };

  const downloadPoster = () => {
    const a = document.createElement('a');
    a.href = '/share-poster.png';
    a.download = 'zakat-international-poster.png';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // Clipboard fallback chain — modern `navigator.clipboard` is gated behind
  // a secure context (HTTPS) AND a permissive `clipboard-write` Permissions
  // Policy. Several hosts (sandboxed iframes, some preview environments,
  // older mobile browsers, in-app webviews like Instagram) silently block
  // it. We try in order:
  //   1. `navigator.clipboard.writeText` (best)
  //   2. The legacy `document.execCommand('copy')` on a hidden <textarea>
  //   3. A `window.prompt()` so the user can hand-select + Ctrl-C
  const fallbackCopy = (value) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = value;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '-9999px';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand && document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  };

  const handleCopy = async () => {
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(finalUrl);
        ok = true;
      }
    } catch { /* fall through to legacy path */ }
    if (!ok) ok = fallbackCopy(finalUrl);
    if (!ok) {
      try { window.prompt('Copy this link (Ctrl/\u2318 + C):', finalUrl); } catch { /* ignore */ }
      return;
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  if (variant === 'qr-only') {
    return (
      <div className="inline-flex flex-col items-center gap-2 p-3 bg-white rounded-xl border border-stone-200" data-testid={`${testid}-qr-block`}>
        <QRCodeSVG value={finalUrl} size={140} level="M" bgColor="#FFFFFF" fgColor="#0D1520" />
        <div className="text-[10px] font-mono uppercase tracking-wider text-stone-500 max-w-[140px] truncate" title={finalUrl}>{finalUrl.replace(/^https?:\/\//, '')}</div>
      </div>
    );
  }

  if (variant === 'inline') {
    // Icon button base — smaller on mobile (7×7 = 28px) so the row fits
    // one line on 375px viewports; grows to 8×8 (32px) on ≥sm.
    const btnBase = 'inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full border transition-colors shrink-0';
    const iconLinks = links.map((l) => (
      <a
        key={l.id}
        href={l.href}
        target={l.id === 'sms' || l.id === 'email' ? '_self' : '_blank'}
        rel="noopener noreferrer"
        data-testid={`${testid}-${l.id}`}
        title={`Share via ${l.label}`}
        aria-label={`Share via ${l.label}`}
        className={`${btnBase} border-stone-200 bg-white ${l.colour} ${l.hoverBg}`}
      >
        <l.Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
      </a>
    ));
    return (
      <div className="inline-flex items-center gap-1 sm:gap-1.5 flex-nowrap sm:flex-wrap" data-testid={`${testid}-inline`}>
        {!compact && (
          <button
            onClick={handleCopy}
            data-testid={`${testid}-copy`}
            title="Copy link"
            aria-label="Copy link"
            className={`${btnBase} border-stone-200 bg-white text-stone-700 hover:text-[#0D1520] hover:border-[#0D4733] hover:bg-[#F3EFEA]`}
          >
            {copied ? <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#0D4733]" /> : <LinkIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
          </button>
        )}
        {iconLinks}
        {!compact && !hideExtras && (
          <button
            onClick={downloadPoster}
            data-testid={`${testid}-poster`}
            title="Download share poster"
            aria-label="Download share poster"
            className={`${btnBase} border-gold-500/60 bg-gold-50 text-[#4A5568] hover:bg-gold-100 hover:border-gold-500`}
          >
            <ImageIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </button>
        )}
        {!compact && !hideExtras && (
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid={`${testid}-qr-toggle`}
            title="Show QR code"
            aria-label="Show QR code"
            className={`${btnBase} border-stone-200 bg-white text-stone-700 hover:text-[#0D1520] hover:border-[#0D4733] hover:bg-[#F3EFEA]`}
          >
            <QrCode className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </button>
        )}
        {!compact && (
          <button
            type="button"
            onClick={handleNativeShare}
            data-testid={`${testid}-native`}
            aria-label="Open native share sheet"
            title="Share"
            className="inline-flex items-center gap-1.5 h-7 sm:h-9 px-2.5 sm:px-3 rounded-full border border-[#CBD2D9] bg-[#EDF5F1] text-[#0D1520] hover:bg-[#EDF5F1] text-[11px] sm:text-xs font-semibold transition-colors shrink-0"
          >
            <Share2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            <span className="hidden sm:inline">Share</span>
          </button>
        )}
        {open && (
          <div className="basis-full mt-2 inline-flex justify-start" data-testid={`${testid}-qr-popover`}>
            <div className="inline-flex flex-col items-center gap-2 p-3 bg-white rounded-xl border border-stone-200">
              <QRCodeSVG value={finalUrl} size={140} level="M" bgColor="#FFFFFF" fgColor="#0D1520" />
              <div className="text-[10px] font-mono uppercase tracking-wider text-stone-500 max-w-[140px] truncate" title={finalUrl}>{finalUrl.replace(/^https?:\/\//, '')}</div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // variant === 'button'
  return (
    <div className="relative inline-block" data-testid={`${testid}-button-wrap`}>
      <Button
        onClick={handleNativeShare}
        data-testid={`${testid}-button`}
        variant="outline"
        className="border-[#CBD2D9] text-[#0D1520] hover:bg-[#F3EFEA]"
      >
        <Share2 className="w-4 h-4 mr-1.5" /> Share
      </Button>
      {open && (
        <div data-testid={`${testid}-dropdown`} className="absolute right-0 mt-2 z-30 w-72 rounded-xl bg-white border border-stone-200 shadow-xl p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-mono uppercase tracking-wider text-stone-500">Share this page</span>
            <button onClick={() => setOpen(false)} className="text-stone-400 hover:text-stone-700"><X className="w-3.5 h-3.5" /></button>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <button onClick={handleCopy} data-testid={`${testid}-copy`} className="flex items-center gap-1.5 px-2.5 py-2 rounded-md hover:bg-[#F3EFEA] text-stone-700 hover:text-[#0D1520] text-sm text-left">
              {copied ? <Check className="w-4 h-4 text-[#0D4733]" /> : <LinkIcon className="w-4 h-4" />} {copied ? 'Copied!' : 'Copy link'}
            </button>
            {links.map((l) => (
              <a
                key={l.id}
                href={l.href}
                target={l.id === 'sms' || l.id === 'email' ? '_self' : '_blank'}
                rel="noopener noreferrer"
                data-testid={`${testid}-${l.id}`}
                className={`flex items-center gap-1.5 px-2.5 py-2 rounded-md hover:bg-[#F3EFEA] ${l.colour} text-sm`}
              >
                <l.Icon className="w-4 h-4" /> {l.label}
              </a>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-stone-100 flex flex-col items-center gap-2">
            <QRCodeSVG value={finalUrl} size={120} level="M" bgColor="#FFFFFF" fgColor="#0D1520" />
            <div className="text-[10px] font-mono uppercase tracking-wider text-stone-500">Scan to open on phone</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShareButtons;
